/**
 * Normalizza risposte GET /Crash/package-exports (contratto: body.items + total_count).
 */

/**
 * @param {unknown} data
 * @returns {Record<string, unknown>|null}
 */
function unwrapApiPayload(data) {
  if (data == null || typeof data !== 'object') return null
  if ('responseData' in data) {
    const inner = /** @type {Record<string, unknown>} */ (data).responseData
    return typeof inner === 'object' && inner !== null ? inner : null
  }
  return /** @type {Record<string, unknown>} */ (data)
}

/**
 * @param {unknown} data
 * @returns {{ items: unknown[], total: number }}
 */
export function normalizePackageExportsListResponse(data) {
  const root = unwrapApiPayload(data)
  if (!root) {
    return { items: [], total: 0 }
  }

  if (Array.isArray(root)) {
    return { items: root, total: root.length }
  }

  const items = Array.isArray(root.items) ? root.items : []
  const total = Number(root.total_count ?? root.total ?? root.totalCount ?? items.length)

  return {
    items,
    total: Number.isFinite(total) ? total : items.length,
  }
}

/**
 * @typedef {Object} PackageImportSummary
 * @property {number} [imported]
 * @property {number} [files_total]
 * @property {number} [duplicates_skipped]
 * @property {number} [errors]
 */

/**
 * @param {unknown} row
 * @returns {{
 *   id: string,
 *   packageId: string,
 *   date: string | null,
 *   organizationId: number | null,
 *   userId: number | null,
 *   records: number | null,
 *   importSummary: PackageImportSummary | null,
 *   status: string | null,
 *   batchId: string,
 *   raw: unknown,
 * }}
 */
export function mapPackageExportGridRow(row) {
  if (typeof row !== 'object' || row === null) {
    return {
      id: '',
      packageId: '',
      date: null,
      organizationId: null,
      userId: null,
      records: null,
      importSummary: null,
      status: null,
      batchId: '',
      raw: row,
    }
  }

  const r = /** @type {Record<string, unknown>} */ (row)
  const importSummary =
    typeof r.import_summary === 'object' && r.import_summary !== null
      ? /** @type {PackageImportSummary} */ (r.import_summary)
      : null

  const packageId = String(r.package_id ?? '')

  return {
    id: packageId,
    packageId,
    date: r.exported_at != null ? String(r.exported_at) : null,
    organizationId:
      r.source_organization_id != null ? Number(r.source_organization_id) : null,
    userId: r.created_by_user_id != null ? Number(r.created_by_user_id) : null,
    records: r.files_count != null ? Number(r.files_count) : null,
    importSummary,
    status: r.import_status != null ? String(r.import_status) : null,
    batchId: r.import_batch_id != null ? String(r.import_batch_id) : '',
    raw: row,
  }
}

/**
 * @param {unknown[]} items
 * @returns {ReturnType<typeof mapPackageExportGridRow>[]}
 */
export function mapPackageExportGridRows(items) {
  return items.map((row) => mapPackageExportGridRow(row))
}

/**
 * @param {unknown} data
 * @returns {{
 *   packageId: string | null,
 *   importBatchId: string | null,
 *   importStatus: string | null,
 *   totalCount: number,
 *   items: unknown[],
 * }}
 */
export function parsePackageExportCrashesResponse(data) {
  const root = unwrapApiPayload(data)
  if (!root) {
    return {
      packageId: null,
      importBatchId: null,
      importStatus: null,
      totalCount: 0,
      items: [],
    }
  }

  const items = Array.isArray(root.items) ? root.items : []
  const totalCount = Number(root.total_count ?? items.length)

  return {
    packageId: root.package_id != null ? String(root.package_id) : null,
    importBatchId: root.import_batch_id != null ? String(root.import_batch_id) : null,
    importStatus: root.import_status != null ? String(root.import_status) : null,
    totalCount: Number.isFinite(totalCount) ? totalCount : items.length,
    items,
  }
}

/**
 * @param {unknown} row
 */
export function mapPackageExportCrashRow(row) {
  if (typeof row !== 'object' || row === null) {
    return {
      id: '',
      crashId: '',
      reportDate: null,
      userId: null,
      importedAt: null,
      importedByUserId: null,
    }
  }

  const r = /** @type {Record<string, unknown>} */ (row)
  const crashId = String(r.crash_id ?? '')

  return {
    id: crashId,
    crashId,
    reportDate: r.report_date != null ? String(r.report_date) : null,
    userId: r.user_id != null ? Number(r.user_id) : null,
    importedAt: r.imported_at != null ? String(r.imported_at) : null,
    importedByUserId:
      r.imported_by_user_id != null ? Number(r.imported_by_user_id) : null,
  }
}

/**
 * @param {unknown[]} items
 */
export function mapPackageExportCrashRows(items) {
  return items.map((row) => mapPackageExportCrashRow(row))
}

/**
 * @param {unknown} data
 * @returns {Record<string, unknown> | null}
 */
export function normalizePackageExportDetailResponse(data) {
  return unwrapApiPayload(data)
}
